import { useEffect, useRef, useState } from 'react';
import { Ruler } from '@/components/icons';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import type { Item } from '@/api/items';
import {
  buildCalibrationHtml,
  buildLabelPrintHtml,
  escapeHtml,
  openPrintWindow,
  THERMAL_LABEL_PRESETS,
} from '@/lib/labelPrint';
import { LabelsPerRowToggle } from '@/components/ui/LabelsPerRowToggle';

interface Props {
  open: boolean;
  item: Item | null;
  onClose: () => void;
}

type LabelSize = typeof THERMAL_LABEL_PRESETS[number]['key'] | 'custom';
const SIZES = THERMAL_LABEL_PRESETS;

export function ItemCodesModal({ open, item, onClose }: Props) {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const [qrDataUrl, setQrDataUrl]           = useState('');
  const [barcodeDataUrl, setBarcodeDataUrl] = useState('');
  const [labelSize, setLabelSize]           = useState<LabelSize>('80x50');
  const [perRow, setPerRow] = useState<1 | 2>(1);
  const [customW, setCustomW]               = useState('70');
  const [customH, setCustomH]               = useState('40');
  const [qty, setQty]                       = useState('1');

  // Human-readable SKU (e.g. BERLIN_160X230_MIX) — shown as a text label.
  const code = item?.code ?? '';
  // What actually gets encoded into the QR + CODE128 strip. Prefer the
  // dedicated barcode field the item master captures (e.g. GH002606);
  // fall back to the SKU if no barcode was assigned.
  const encoded = item?.barcode?.trim() || code;

  useEffect(() => {
    if (!open || !encoded) return;

    if (barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, encoded, {
          format: 'CODE128',
          width: 1.8,
          height: 40,
          displayValue: false,
          margin: 4,
          background: '#ffffff',
          lineColor: '#000000',
        });
        const svgData = new XMLSerializer().serializeToString(barcodeRef.current);
        setBarcodeDataUrl('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData));
      } catch { /* invalid */ }
    }

    QRCode.toDataURL(encoded, { width: 200, margin: 1, color: { dark: '#000000', light: '#ffffff' } })
      .then(setQrDataUrl);
  }, [open, encoded]);

  const esc = escapeHtml;

  function singleLabelHtml(fw: number, fh: number, itemBarcodeUrl: string, itemQrUrl: string): string {
    if (!item) return '';
    const sizeParts = [item.width, item.length].filter(Boolean).join('x');
    const sizeStr   = sizeParts ? `${sizeParts} ${item.sizeUnit ?? ''}`.trim() : '';
    const mrpStr    = item.mrp  ? `MRP : \u20B9 ${Number(item.mrp).toLocaleString('en-IN')}` : '';
    // Responsive scaling \u2014 same rule as GRN/Location labels.
    const s = Math.min(fw / 60, fh / 40);
    const baseFs = Math.max(3.5, 6 * s);
    const shortSide = Math.min(fw, fh);
    const qrSide = shortSide * 0.38;
    const bcHeight = shortSide * 0.28;
    // Dynamic font: scale down for long names / codes
    const nameLen = item.name.length;
    const nameFs = nameLen > 40 ? Math.max(3.5, (baseFs - 1) * (40 / nameLen))
                 : nameLen > 24 ? baseFs
                 :                baseFs + 1;
    // Big bold caption on the label is the encoded value (item.barcode when
    // set, else SKU) — matches what a scanner reads. Font scales down for
    // longer values so the label stays legible.
    const codeLen = encoded.length;
    const codeFs = codeLen > 22 ? baseFs + 1 : codeLen > 14 ? baseFs + 2 : baseFs + 4;
    // Caption under the QR + barcode should be the ENCODED value so the
    // printed text matches what a scanner would decode.
    const qrCaption = encoded.length > 14 ? encoded.slice(0, 13) + '\u2026' : encoded;
    return `
<div class="cell">
  <div class="top-row">
    <div class="info">
      <div class="item-name" style="font-size:${nameFs.toFixed(2)}pt">${esc(item.name)}</div>
      <div class="item-cat" style="font-size:${baseFs.toFixed(2)}pt">${esc(item.category)}</div>
      ${sizeStr ? `<div class="item-size" style="font-size:${baseFs.toFixed(2)}pt">Size : ${esc(sizeStr)}</div>` : ''}
      <div class="item-code" style="font-size:${codeFs.toFixed(2)}pt">${esc(encoded)}</div>
      ${mrpStr ? `<div class="item-mrp" style="font-size:${baseFs.toFixed(2)}pt">${esc(mrpStr)}</div>` : ''}
    </div>
    <div class="qr-block">
      <img src="${itemQrUrl}" style="width:${qrSide.toFixed(2)}mm;height:${qrSide.toFixed(2)}mm" />
      <div class="qr-text" style="font-size:${Math.max(2.5, baseFs - 2).toFixed(2)}pt;max-width:${qrSide.toFixed(2)}mm">${esc(qrCaption)}</div>
    </div>
  </div>
  <div class="barcode-row">
    <img src="${itemBarcodeUrl}" style="max-height:${bcHeight.toFixed(2)}mm" />
    <div class="barcode-code" style="font-size:${Math.max(3, baseFs - 1).toFixed(2)}pt">${esc(encoded)}</div>
  </div>
</div>`;
  }

  function resolvedSize() {
    const w = labelSize === 'custom' ? Math.max(10, Number(customW) || 70) : SIZES.find((s) => s.key === labelSize)!.w;
    const h = labelSize === 'custom' ? Math.max(10, Number(customH) || 40) : SIZES.find((s) => s.key === labelSize)!.h;
    // "2 labels" splits the chosen size into two equal labels side by side.
    const across = perRow;
    return { w: w / across, h, across, fullW: w };
  }

  function printCalibration() {
    const { w, h, across } = resolvedSize();
    openPrintWindow(buildCalibrationHtml({ w, h, across }), w, h);
  }

  async function printLabel() {
    const { w, h, across } = resolvedSize();
    const copies = Math.max(1, Math.min(500, Number(qty) || 1));

    // Generate barcode and QR for the encoded value (item.barcode when set,
    // otherwise item.code) once, reuse for all copies.
    const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    let itemBarcodeUrl = '';
    try {
      JsBarcode(svgEl, encoded, { format: 'CODE128', width: 1.8, height: 40, displayValue: false, margin: 4, background: '#ffffff', lineColor: '#000000' });
      itemBarcodeUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svgEl));
    } catch { /* skip */ }

    const itemQrUrl = await QRCode.toDataURL(encoded, { width: 200, margin: 1, color: { dark: '#000000', light: '#ffffff' } });

    const cells: string[] = [];
    for (let i = 0; i < copies; i++) {
      cells.push(singleLabelHtml(w, h, itemBarcodeUrl, itemQrUrl));
    }

    const extraCss = `
      .item-name { font-weight: normal; line-height: 1.25; overflow-wrap: break-word; word-break: break-word; }
      .item-cat  { color: #333; }
      .item-size { color: #333; }
      .item-code { font-weight: 900; margin-top: 1mm; overflow-wrap: break-word; word-break: break-all; line-height: 1.2; }
      .item-mrp  { font-weight: bold; margin-top: 0.5mm; }
      .qr-text   { color: #555; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .barcode-code { text-align: center; color: #333; margin-top: 0.3mm; overflow-wrap: break-word; word-break: break-all; line-height: 1.3; }
    `;
    const html = buildLabelPrintHtml({
      w, h, across, extraCss,
      title: `Item Label — ${encoded}`,
      cellsHtml: cells.join(''),
    });
    openPrintWindow(html, w, h);
  }

  if (!open || !item) return null;

  const sizeParts = [item.width, item.length].filter(Boolean).join('x');
  const sizeLabel = sizeParts ? `${sizeParts} ${item.sizeUnit ?? ''}`.trim() : '';
  const mrpLabel  = item.mrp  ? `MRP : \u20B9 ${Number(item.mrp).toLocaleString('en-IN')}` : '';

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.65)' }}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
          <div>
            <div className="text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}>Label Preview</div>
            <div className="text-[11px] font-mono" style={{ color: 'var(--text-muted)' }}>{code} · {item.name}</div>
          </div>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg opacity-50 hover:opacity-100" style={{ background: 'var(--bg-soft)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Quantity row — just below header */}
        <div className="flex items-center justify-end gap-2 border-b px-5 py-2" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
          <span className="text-[11px] font-bold" style={{ color: 'var(--text-secondary)' }}>Set Quantity</span>
          <input
            type="number" min="1" max="500"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="w-16 rounded-md border px-2 py-1 text-xs text-center font-bold"
            style={{ background: 'var(--bg-soft)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          />
        </div>

        <div className="flex flex-col items-center px-6 py-5 gap-5">
          {/* Preview card — exact label box structure */}
          <div className="bg-white text-black font-sans shadow-lg" style={{ width: 300, border: '1.5px solid #555', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Top row */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
              {/* Info left */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                <div style={{ fontSize: item.name.length > 24 ? 8.5 : 10, fontWeight: 'normal', lineHeight: 1.3, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{item.name}</div>
                <div style={{ fontSize: 8.5, color: '#333' }}>{item.category}</div>
                {sizeLabel && <div style={{ fontSize: 8.5, color: '#444' }}>Size : {sizeLabel}</div>}
                <div style={{ fontSize: encoded.length > 22 ? 9 : encoded.length > 14 ? 11 : 13, fontWeight: 900, marginTop: 3, overflowWrap: 'break-word', wordBreak: 'break-all', lineHeight: 1.2 }}>{encoded}</div>
                {mrpLabel && <div style={{ fontSize: 9, fontWeight: 'bold', marginTop: 2 }}>{mrpLabel}</div>}
              </div>
              {/* QR right */}
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {qrDataUrl
                  ? <img src={qrDataUrl} alt="QR" style={{ width: 72, height: 72 }} />
                  : <div style={{ width: 72, height: 72, background: '#eee', borderRadius: 4 }} />
                }
                <span style={{ fontSize: 6.5, color: '#555', maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{encoded}</span>
              </div>
            </div>
            {/* Barcode full width */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: 4 }}>
              <svg ref={barcodeRef} style={{ width: '100%', display: 'block' }} />
            </div>
          </div>

          {/* Size selector */}
          <div className="w-full">
            <div className="mb-1.5 text-[11px] font-bold" style={{ color: 'var(--text-secondary)' }}>Print Size</div>
            <div className="grid grid-cols-2 gap-2">
              {SIZES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setLabelSize(s.key)}
                  className="rounded-lg px-3 py-2 text-[11px] font-bold border transition"
                  style={{
                    background:  labelSize === s.key ? 'var(--accent)' : 'var(--bg-card)',
                    color:       labelSize === s.key ? '#000'          : 'var(--text-secondary)',
                    borderColor: labelSize === s.key ? 'var(--accent)' : 'var(--border-color)',
                  }}
                >
                  {s.label}
                </button>
              ))}
              {/* Custom size */}
              <button
                onClick={() => setLabelSize('custom')}
                className="rounded-lg px-3 py-2 text-[11px] font-bold border transition"
                style={{
                  background:  labelSize === 'custom' ? 'var(--accent)' : 'var(--bg-card)',
                  color:       labelSize === 'custom' ? '#000'          : 'var(--text-secondary)',
                  borderColor: labelSize === 'custom' ? 'var(--accent)' : 'var(--border-color)',
                }}
              >
                +
              </button>
            </div>
            {labelSize === 'custom' && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number" min="10" max="300"
                  value={customW}
                  onChange={(e) => setCustomW(e.target.value)}
                  className="w-20 rounded-md border px-2 py-1 text-xs text-center"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  placeholder="W"
                />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>mm × </span>
                <input
                  type="number" min="10" max="300"
                  value={customH}
                  onChange={(e) => setCustomH(e.target.value)}
                  className="w-20 rounded-md border px-2 py-1 text-xs text-center"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  placeholder="H"
                />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>mm</span>
              </div>
            )}
            <LabelsPerRowToggle value={perRow} onChange={setPerRow} w={resolvedSize().fullW} h={resolvedSize().h} />
          </div>

          {/* Downloads */}
          <div className="flex gap-5 text-[11px]">
            {barcodeDataUrl && (
              <a href={barcodeDataUrl} download={`${encoded}-barcode.svg`} className="font-bold text-accent hover:underline">↓ Barcode SVG</a>
            )}
            {qrDataUrl && (
              <a href={qrDataUrl} download={`${encoded}-qr.png`} className="font-bold text-accent hover:underline">↓ QR PNG</a>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t px-5 py-3" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
          <button onClick={onClose} className="rounded-lg px-4 py-1.5 text-xs font-bold" style={{ background: 'var(--bg-soft)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
            Close
          </button>
          <button
            onClick={printCalibration}
            title="Prints one sized rectangle with corner marks + mm rulers so you can hold it against a real sticker to verify the size"
            className="rounded-lg px-4 py-1.5 text-xs font-bold"
            style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.5)', color: 'rgb(245,158,11)' }}
          >
            <Ruler size={13} className="mr-1 inline-block align-[-2px]" /> Calibration
          </button>
          <button onClick={printLabel} className="rounded-lg px-5 py-1.5 text-xs font-extrabold text-black" style={{ background: 'var(--accent)' }}>
            Print Label
          </button>
        </div>
      </div>
    </div>
  );
}
